import {
  Button,
  Dialog,
  DialogSurface,
  FluentProvider,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import type { IMetadata } from '@moko/utils';
import { FormPreview } from './FormPreview';
import type { IOtherInfoProps } from '../OtherInfo/types';
import { Empty } from '../Empty/Empty';
import useFetch from '../hooks/useFetch';
import { useActiveTheme } from '../theme/ThemeContext';

export type GetFormPreviewMetadata = (
  abortController: AbortController,
  entityType?: string,
  allFields?: boolean,
  name?: string
) => Promise<IMetadata>;

export interface FormPreviewDialogProps {
  open: boolean;
  onDismiss: () => void;
  title?: string;
  entity: string;
  name?: string;
  getMetadata: GetFormPreviewMetadata;
  otherInfo?: Record<string, unknown>;
  fetchDetailsLabel: string;
  onFetchDetails: () => void;
  readOnly?: boolean;
  onFetchUsers?: IOtherInfoProps['onFetchUsers'];
  onFetchAutoComplete?: IOtherInfoProps['onFetchAutoComplete'];
}

const useStyles = makeStyles({
  surface: {
    width: 'min(720px, calc(100vw - 32px))',
    maxWidth: '720px',
    height: '80vh',
    maxHeight: '80vh',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
  },
  body: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: `0 ${tokens.spacingHorizontalL}`,
  },
  fill: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  footer: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  status: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
});

export const FormPreviewDialog = ({
  open,
  onDismiss,
  title,
  entity,
  name,
  getMetadata,
  otherInfo,
  fetchDetailsLabel,
  onFetchDetails,
  readOnly = true,
  onFetchUsers,
  onFetchAutoComplete,
}: FormPreviewDialogProps) => {
  const theme = useActiveTheme();
  const { t } = useTranslation();
  const styles = useStyles();
  const { data, loading, error } = useFetch<IMetadata>({
    queryKey: [entity, name],
    enabled: open && !!name,
    queryFn: (abortController) =>
      getMetadata(abortController, entity, undefined, name),
  });
  const hasFields = (data?.fields ?? []).some(
    (field) => field.effect !== 'Hide'
  );

  return (
    <FluentProvider theme={theme}>
      <Dialog
        open={open}
        onOpenChange={(_, dialogData) => {
          if (!dialogData.open) onDismiss();
        }}
      >
        <DialogSurface className={styles.surface}>
          <div className={styles.root}>
            <div className={styles.header}>
              <Text weight="semibold" size={500}>
                {title ?? name ?? 'Preview'}
              </Text>
              <Button
                appearance="subtle"
                aria-label={t('close', 'Close')}
                icon={<Dismiss24Regular />}
                onClick={onDismiss}
              />
            </div>
            <div className={styles.body}>
              {loading ? (
                <div className={styles.status}>
                  <Spinner size="medium" />
                </div>
              ) : error ? (
                <Text>{error.message}</Text>
              ) : hasFields ? (
                <div className={styles.fill}>
                  <FormPreview
                    metadata={data!}
                    otherInfo={otherInfo}
                    mode="mobile"
                    contained
                    readOnly={readOnly}
                    onFetchUsers={onFetchUsers}
                    onFetchAutoComplete={onFetchAutoComplete}
                  />
                </div>
              ) : (
                <Empty title="No form to preview" />
              )}
            </div>
            <div className={styles.footer}>
              <Button appearance="secondary" onClick={onDismiss}>
                {t('cancel', 'Cancel')}
              </Button>
              <Button appearance="primary" onClick={onFetchDetails}>
                {fetchDetailsLabel}
              </Button>
            </div>
          </div>
        </DialogSurface>
      </Dialog>
    </FluentProvider>
  );
};
